import React, { useState, useEffect, useMemo } from 'react';
import { studentApi } from '../../services/api';

/**
 * Günlük Verilerim — student-only personal study log. Persisted in the
 * student_daily_entries table via /student/daily-entries (was localStorage
 * before Phase 6). The backend deliberately does NOT join this into any
 * performance rollup; the page UI restates that contract.
 *
 * If a previous version of this app stored entries in localStorage, the
 * one-shot migration below copies them up on first load and clears the
 * local key so the user's history survives the switch.
 */

interface DailyEntry {
  id: string;
  date: string;       // YYYY-MM-DD
  studyMinutes: number;
  questionsSolved: number;
  mood: 'good' | 'ok' | 'bad' | '';
  notes: string;
  createdAt: string;
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// One-shot migration of the old localStorage entries into the API. Runs once
// per browser per user — the localStorage key is cleared after success so we
// don't double-import.
const LEGACY_PREFIX = 'dijipo:daily-data';
async function migrateLegacyEntriesIfAny(userId: string): Promise<number> {
  const key = `${LEGACY_PREFIX}:${userId}`;
  let raw: string | null = null;
  try { raw = localStorage.getItem(key); } catch { return 0; }
  if (!raw) return 0;
  let arr: any[] = [];
  try { arr = JSON.parse(raw) ?? []; } catch { localStorage.removeItem(key); return 0; }
  if (!Array.isArray(arr) || arr.length === 0) { localStorage.removeItem(key); return 0; }
  let migrated = 0;
  for (const e of arr) {
    try {
      await studentApi.createDailyEntry({
        entryDate: e.date,
        studyMinutes: Number(e.studyMinutes ?? 0),
        questionsSolved: Number(e.questionsSolved ?? 0),
        mood: e.mood ?? '',
        notes: e.notes ?? '',
      });
      migrated++;
    } catch { /* keep going */ }
  }
  try { localStorage.removeItem(key); } catch { /* ignore */ }
  return migrated;
}

const MOOD_LABEL: Record<DailyEntry['mood'], string> = {
  good: 'İyi',
  ok: 'Orta',
  bad: 'Kötü',
  '': '—',
};

const StudentDailyData: React.FC = () => {
  const [entries, setEntries] = useState<DailyEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [date, setDate] = useState(todayISO());
  const [studyMinutes, setStudyMinutes] = useState('');
  const [questionsSolved, setQuestionsSolved] = useState('');
  const [mood, setMood] = useState<DailyEntry['mood']>('');
  const [notes, setNotes] = useState('');

  const reload = () => {
    setLoading(true);
    studentApi.listDailyEntries()
      .then((r) => {
        setEntries(r.data.map((e) => ({
          id: e.id,
          date: e.entry_date,
          studyMinutes: e.study_minutes,
          questionsSolved: e.questions_solved,
          mood: (e.mood as DailyEntry['mood']) ?? '',
          notes: e.notes,
          createdAt: e.created_at,
        })));
      })
      .catch((e: any) => setError(e?.response?.data?.error ?? e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    // Migrate legacy localStorage entries once per user. Reads /auth/me uuid
    // out of the cached auth user since the migration helper is keyed by it.
    let cancelled = false;
    (async () => {
      try {
        const cached = localStorage.getItem('dijipo_user');
        const u = cached ? JSON.parse(cached) : null;
        if (u?.id) await migrateLegacyEntriesIfAny(u.id);
      } catch { /* ignore */ }
      if (!cancelled) reload();
    })();
    return () => { cancelled = true; };
  }, []);

  const totals = useMemo(() => {
    const totalMinutes = entries.reduce((s, e) => s + (e.studyMinutes || 0), 0);
    const totalQuestions = entries.reduce((s, e) => s + (e.questionsSolved || 0), 0);
    return { totalMinutes, totalQuestions, count: entries.length };
  }, [entries]);

  const handleAdd = async () => {
    setError(null);
    setSaving(true);
    try {
      await studentApi.createDailyEntry({
        entryDate: date || todayISO(),
        studyMinutes: parseInt(studyMinutes, 10) || 0,
        questionsSolved: parseInt(questionsSolved, 10) || 0,
        mood: mood || undefined,
        notes: notes.trim() || undefined,
      });
      reload();
      setStudyMinutes(''); setQuestionsSolved(''); setMood(''); setNotes('');
      setDate(todayISO());
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setError(null);
    try {
      await studentApi.deleteDailyEntry(id);
      setEntries((prev) => prev.filter(e => e.id !== id));
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e.message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-[20px] lg:text-[24px] font-semibold text-[#2b2b2b]">Günlük Verilerim</h1>
        <span className="text-[13px] text-gray-500">
          Bu alan sadece size özeldir — performansa işlenmez.
        </span>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-[#fafafa] rounded-[16px] p-4 text-center" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.08)' }}>
          <p className="text-[12px] text-gray-500">Toplam Kayıt</p>
          <p className="text-[24px] font-bold text-[#2b2b2b]">{totals.count}</p>
        </div>
        <div className="bg-[#fafafa] rounded-[16px] p-4 text-center" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.08)' }}>
          <p className="text-[12px] text-gray-500">Toplam Çalışma (dk)</p>
          <p className="text-[24px] font-bold text-[#3973bf]">{totals.totalMinutes}</p>
        </div>
        <div className="bg-[#fafafa] rounded-[16px] p-4 text-center" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.08)' }}>
          <p className="text-[12px] text-gray-500">Toplam Çözülen Soru</p>
          <p className="text-[24px] font-bold text-[#e5599b]">{totals.totalQuestions}</p>
        </div>
      </div>

      {/* Entry form */}
      <div className="bg-[#fafafa] rounded-[16px] p-5 mb-6" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.08)' }}>
        <p className="text-[15px] font-semibold text-[#2b2b2b] mb-4">Yeni Kayıt Ekle</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
          <div>
            <label className="text-[12px] font-medium text-gray-600 mb-1 block">Tarih</label>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              className="w-full bg-white rounded-[10px] h-[40px] px-3 border border-gray-200 text-[14px] outline-none"
            />
          </div>
          <div>
            <label className="text-[12px] font-medium text-gray-600 mb-1 block">Çalışma (dakika)</label>
            <input
              type="number"
              min={0}
              value={studyMinutes}
              onChange={e => setStudyMinutes(e.target.value)}
              placeholder="Örn: 90"
              className="w-full bg-white rounded-[10px] h-[40px] px-3 border border-gray-200 text-[14px] outline-none"
            />
          </div>
          <div>
            <label className="text-[12px] font-medium text-gray-600 mb-1 block">Çözülen Soru</label>
            <input
              type="number"
              min={0}
              value={questionsSolved}
              onChange={e => setQuestionsSolved(e.target.value)}
              placeholder="Örn: 40"
              className="w-full bg-white rounded-[10px] h-[40px] px-3 border border-gray-200 text-[14px] outline-none"
            />
          </div>
          <div>
            <label className="text-[12px] font-medium text-gray-600 mb-1 block">Günün Nasıldı?</label>
            <select
              value={mood}
              onChange={e => setMood(e.target.value as DailyEntry['mood'])}
              className="w-full bg-white rounded-[10px] h-[40px] px-3 border border-gray-200 text-[14px] outline-none"
            >
              <option value="">—</option>
              <option value="good">İyi</option>
              <option value="ok">Orta</option>
              <option value="bad">Kötü</option>
            </select>
          </div>
        </div>
        <div className="mb-3">
          <label className="text-[12px] font-medium text-gray-600 mb-1 block">Kişisel Not</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            placeholder="Bugün nasıldı? Neye çalıştın?"
            className="w-full bg-white rounded-[10px] px-3 py-2 border border-gray-200 text-[14px] outline-none resize-none"
          />
        </div>
        <div className="flex justify-end">
          <button
            onClick={handleAdd}
            className="bg-[#3973bf] text-white text-[14px] font-semibold px-6 py-2.5 rounded-[10px] hover:bg-blue-700 transition-colors"
          >
            Kaydet
          </button>
        </div>
      </div>

      {/* Entry list */}
      <h2 className="text-[16px] font-semibold text-[#2b2b2b] mb-3">Geçmiş Kayıtlar</h2>
      {entries.length === 0 ? (
        <p className="text-center text-gray-400 py-10">Henüz kayıt eklenmedi.</p>
      ) : (
        <div className="space-y-2">
          {entries.map(e => (
            <div
              key={e.id}
              className="bg-[#fafafa] rounded-[12px] p-4 flex items-center gap-4"
              style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.06)' }}
            >
              <div className="w-[84px] text-center flex-shrink-0">
                <p className="text-[12px] text-gray-500">Tarih</p>
                <p className="text-[14px] font-semibold text-[#2b2b2b]">{e.date?.slice(0, 10).split('-').reverse().join('.')}</p>
              </div>
              <div className="w-[96px] text-center flex-shrink-0 border-l border-gray-200 pl-4">
                <p className="text-[12px] text-gray-500">Çalışma</p>
                <p className="text-[14px] font-semibold text-[#3973bf]">{e.studyMinutes} dk</p>
              </div>
              <div className="w-[96px] text-center flex-shrink-0 border-l border-gray-200 pl-4">
                <p className="text-[12px] text-gray-500">Soru</p>
                <p className="text-[14px] font-semibold text-[#e5599b]">{e.questionsSolved}</p>
              </div>
              <div className="w-[72px] text-center flex-shrink-0 border-l border-gray-200 pl-4">
                <p className="text-[12px] text-gray-500">Gün</p>
                <p className="text-[14px] font-semibold text-[#2b2b2b]">{MOOD_LABEL[e.mood]}</p>
              </div>
              <div className="flex-1 min-w-0 border-l border-gray-200 pl-4">
                <p className="text-[12px] text-gray-500">Not</p>
                <p className="text-[13px] text-[#2b2b2b] truncate">{e.notes || '—'}</p>
              </div>
              <button
                onClick={() => handleDelete(e.id)}
                aria-label="Sil"
                className="w-[28px] h-[28px] rounded-full hover:bg-red-50 flex items-center justify-center text-[14px] text-gray-400 hover:text-red-500"
              >×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StudentDailyData;
